import theme from 'config/theme'
import {
  CenteredTextSymbolizer,
  LabelRule,
  linear,
  LineLabelSymbolizer,
  LineSymbolizer,
  PaintRule,
  PolygonSymbolizer,
} from 'protomaps-leaflet'

const COLORS = {
  vegetationFill: '#c5e8c5',
  wetlandFill: '#b3d9d9',
  hospitalFill: '#fde3e3',
  cemeteryFill: '#c8dfc8',
  waterFill: '#aadaff',
  buildingFill: '#d9d0c9',
  buildingStroke: '#c8bcae',
  boundary: '#f9574b',
  roadCasing: '#707070',
  motorwayCore: '#809bc0',
  trunkCore: '#fff2af',
  roadCoreWhite: '#ffffff',
  cyclewayCore: '#d1fad1',
  pathCoreLight: '#eeeeee',
  trackCore: '#d79331',
  railCore: '#333333',
  railCasing: '#e6e6e6',
  labelText: '#333333',
  labelHalo: '#ffffff',
} as const

const TYPOGRAPHY = {
  fontFamily: theme.typography.fontFamily,
  lineHeight: Number(theme.typography.lineHeight),
} as const

const TUNNEL_DASH = [4, 3]

const ADMIN_LEVEL_STYLES: Record<number, { dash?: number[]; width: number; minzoom?: number }> = {
  1: { dash: [15, 5, 5, 5], width: 1.4 },
  2: { dash: [15, 15], width: 1.4 },
  3: { width: 1, minzoom: 2 },
  4: { dash: [15, 5, 5, 5], width: 1, minzoom: 2 },
  5: { dash: [15, 15], width: 0.8, minzoom: 5 },
  6: { dash: [5, 5], width: 0.8, minzoom: 6 },
  7: { width: 0.5, minzoom: 8 },
  8: { dash: [15, 5, 5, 5], width: 0.5, minzoom: 9 },
  9: { dash: [15, 15], width: 0.5, minzoom: 9 },
  10: { dash: [5, 20], width: 0.5, minzoom: 10 },
  11: { dash: [1, 5], width: 0.5, minzoom: 11 },
}

const ROAD_CLASS_STYLE: {
  classes: string[]
  core: string
  minzoom?: number
  casingWidth: (z: number) => number
  coreWidth: (z: number) => number
}[] = [
  {
    classes: ['motorway'],
    core: COLORS.motorwayCore,
    casingWidth: linear([
      [3, 0.6],
      [14, 2.4],
    ]),
    coreWidth: linear([
      [3, 0.4],
      [14, 1.8],
    ]),
  },
  {
    classes: ['trunk'],
    core: COLORS.trunkCore,
    casingWidth: linear([
      [4, 0.5],
      [14, 2.2],
    ]),
    coreWidth: linear([
      [4, 0.3],
      [14, 1.6],
    ]),
  },
  {
    classes: ['primary'],
    core: COLORS.roadCoreWhite,
    minzoom: 5,
    casingWidth: linear([
      [5, 0.4],
      [14, 2.0],
    ]),
    coreWidth: linear([
      [5, 0.3],
      [14, 1.5],
    ]),
  },
  {
    classes: ['secondary'],
    core: COLORS.roadCoreWhite,
    minzoom: 7,
    casingWidth: linear([
      [7, 0.4],
      [14, 1.9],
    ]),
    coreWidth: linear([
      [7, 0.3],
      [14, 1.4],
    ]),
  },
  {
    classes: ['tertiary'],
    core: COLORS.roadCoreWhite,
    minzoom: 9,
    casingWidth: linear([
      [9, 0.4],
      [14, 1.7],
    ]),
    coreWidth: linear([
      [9, 0.3],
      [14, 1.3],
    ]),
  },
  {
    classes: ['minor', 'service'],
    core: COLORS.roadCoreWhite,
    minzoom: 11,
    casingWidth: linear([
      [11, 0.4],
      [14, 1.4],
    ]),
    coreWidth: linear([
      [11, 0.3],
      [14, 1.0],
    ]),
  },
  {
    classes: ['busway', 'raceway', 'transit'],
    core: COLORS.roadCoreWhite,
    minzoom: 11,
    casingWidth: linear([
      [11, 0.4],
      [14, 1.3],
    ]),
    coreWidth: linear([
      [11, 0.3],
      [14, 0.9],
    ]),
  },
]

const PATH_SUBCLASS_STYLE: { subclasses: string[]; core: string; width: number; dash?: number[] }[] = [
  { subclasses: ['cycleway'], core: COLORS.cyclewayCore, width: 0.9 },
  { subclasses: ['footway', 'pedestrian', 'corridor'], core: COLORS.pathCoreLight, width: 0.6 },
  { subclasses: ['steps'], core: COLORS.pathCoreLight, width: 0.45, dash: [1.5, 1.5] },
]
const PATH_MINZOOM = 13
const TRACK_MINZOOM = 11
const RAIL_MINZOOM = 6

const PLACE_CLASS_STYLE: { classes: string[]; minzoom: number; fontSize: number; fontWeight: number }[] = [
  { classes: ['city', 'town'], minzoom: 6, fontSize: 13, fontWeight: 700 },
  { classes: ['village', 'hamlet'], minzoom: 10, fontSize: 11, fontWeight: 600 },
  { classes: ['suburb', 'quarter', 'neighbourhood'], minzoom: 13, fontSize: 10, fontWeight: 500 },
]

const STREET_LABEL_CLASSES = ['motorway', 'trunk', 'primary', 'secondary', 'tertiary', 'minor', 'service']
const STREET_LABEL_MINZOOM = 13
const HIGHWAY_LABEL_CLASSES = ['motorway', 'trunk']

function classOf(f: { props: { class?: unknown } }): string {
  return String(f.props.class ?? '')
}

function pathSubclassOf(f: { props: { subclass?: unknown } }): string {
  return String(f.props.subclass ?? '')
}

function isTunnel(f: { props: { brunnel?: unknown } }): boolean {
  return f.props.brunnel === 'tunnel'
}

export const PAINT_RULES: PaintRule[] = [
  {
    dataLayer: 'landcover',
    filter: (_z, f) => f.props.subclass !== 'wetland',
    symbolizer: new PolygonSymbolizer({ fill: COLORS.vegetationFill }),
  },
  {
    dataLayer: 'landcover',
    filter: (_z, f) => f.props.subclass === 'wetland',
    symbolizer: new PolygonSymbolizer({ fill: COLORS.wetlandFill }),
  },
  {
    dataLayer: 'park',
    symbolizer: new PolygonSymbolizer({ fill: COLORS.vegetationFill }),
  },
  {
    dataLayer: 'landuse',
    filter: (_z, f) => f.props.class === 'hospital',
    symbolizer: new PolygonSymbolizer({ fill: COLORS.hospitalFill }),
  },
  {
    dataLayer: 'landuse',
    filter: (_z, f) => f.props.class === 'cemetery',
    symbolizer: new PolygonSymbolizer({ fill: COLORS.cemeteryFill }),
  },
  {
    dataLayer: 'water',
    symbolizer: new PolygonSymbolizer({ fill: COLORS.waterFill }),
  },
  ...Object.entries(ADMIN_LEVEL_STYLES).map(
    ([level, style]): PaintRule => ({
      dataLayer: 'boundary',
      minzoom: style.minzoom,
      filter: (_z, f) => Number(f.props.admin_level) === Number(level),
      symbolizer: new LineSymbolizer({
        color: COLORS.boundary,
        width: style.width,
        dash: style.dash,
        dashColor: COLORS.boundary,
      }),
    })
  ),
  ...ROAD_CLASS_STYLE.map(
    ({ classes, casingWidth, minzoom }): PaintRule => ({
      dataLayer: 'transportation',
      minzoom,
      filter: (_z, f) => isTunnel(f) && classes.includes(classOf(f)),
      symbolizer: new LineSymbolizer({
        color: COLORS.roadCasing,
        width: casingWidth,
        dash: TUNNEL_DASH,
        dashColor: COLORS.roadCasing,
      }),
    })
  ),
  ...ROAD_CLASS_STYLE.map(
    ({ classes, casingWidth, minzoom }): PaintRule => ({
      dataLayer: 'transportation',
      minzoom,
      filter: (_z, f) => !isTunnel(f) && classes.includes(classOf(f)),
      symbolizer: new LineSymbolizer({ color: COLORS.roadCasing, width: casingWidth }),
    })
  ),
  ...ROAD_CLASS_STYLE.map(
    ({ classes, core, coreWidth, minzoom }): PaintRule => ({
      dataLayer: 'transportation',
      minzoom,
      filter: (_z, f) => !isTunnel(f) && classes.includes(classOf(f)),
      symbolizer: new LineSymbolizer({ color: core, width: coreWidth }),
    })
  ),
  {
    dataLayer: 'transportation',
    minzoom: TRACK_MINZOOM,
    filter: (_z, f) => classOf(f) === 'track',
    symbolizer: new LineSymbolizer({ color: COLORS.trackCore, width: 0.9, dash: [4, 2] }),
  },
  ...PATH_SUBCLASS_STYLE.map(
    ({ subclasses, core, width, dash }): PaintRule => ({
      dataLayer: 'transportation',
      minzoom: PATH_MINZOOM,
      filter: (_z, f) => classOf(f) === 'path' && subclasses.includes(pathSubclassOf(f)),
      symbolizer: new LineSymbolizer({ color: core, width, dash }),
    })
  ),
  {
    dataLayer: 'transportation',
    minzoom: RAIL_MINZOOM,
    filter: (_z, f) => classOf(f) === 'rail',
    symbolizer: new LineSymbolizer({ color: COLORS.railCore, width: 0.6 }),
  },
  {
    dataLayer: 'transportation',
    minzoom: RAIL_MINZOOM,
    filter: (_z, f) => classOf(f) === 'rail',
    symbolizer: new LineSymbolizer({ color: COLORS.railCasing, width: 0.4, dash: [3, 3] }),
  },
  {
    dataLayer: 'building',
    minzoom: 13,
    symbolizer: new PolygonSymbolizer({ fill: COLORS.buildingFill, stroke: COLORS.buildingStroke }),
  },
]

export const LABEL_RULES: LabelRule[] = [
  ...PLACE_CLASS_STYLE.map(
    ({ classes, minzoom, fontSize, fontWeight }): LabelRule => ({
      dataLayer: 'place',
      minzoom,
      filter: (_z, f) => classes.includes(classOf(f)),
      symbolizer: new CenteredTextSymbolizer({
        fontFamily: TYPOGRAPHY.fontFamily,
        lineHeight: TYPOGRAPHY.lineHeight,
        fontSize,
        fontWeight,
        fill: COLORS.labelText,
        stroke: COLORS.labelHalo,
        width: 2,
      }),
    })
  ),
  {
    dataLayer: 'transportation_name',
    minzoom: STREET_LABEL_MINZOOM,
    filter: (_z, f) => STREET_LABEL_CLASSES.includes(classOf(f)),
    symbolizer: new LineLabelSymbolizer({
      labelProps: (_z, f) => (f && HIGHWAY_LABEL_CLASSES.includes(classOf(f)) ? ['ref', 'name'] : ['name']),
      fontFamily: TYPOGRAPHY.fontFamily,
      fontSize: 10,
      fill: COLORS.labelText,
      stroke: COLORS.labelHalo,
      width: 2,
    }),
  },
]
